import posthog from 'posthog-js';
import type { AppConfig } from '@/api/config';

/**
 * Thin wrapper around posthog-js. Every export is a no-op until
 * `initAnalytics` has run with a non-null `config.posthog`, which only
 * production serves — dev and alpha never load PostHog or make a network call.
 * Nothing here may throw: analytics must never take the app down.
 */

let initialized = false;

export function isAnalyticsReady() {
  return initialized;
}

export function initAnalytics(config: AppConfig | null | undefined) {
  if (initialized || !config?.posthog) return;
  const { key, host } = config.posthog;
  if (!key || !host) return;
  try {
    posthog.init(key, {
      api_host: host,
      autocapture: true,
      capture_pageview: false,
      respect_dnt: false
    });
    initialized = true;
  } catch {
    /* analytics is best-effort */
  }
}

export function identify(userId: string, props?: { name?: string | null; email?: string | null }) {
  if (!initialized || !userId) return;
  try {
    const clean: Record<string, string> = {};
    if (props?.name) clean.name = props.name;
    if (props?.email) clean.email = props.email;
    posthog.identify(userId, clean);
  } catch {
    /* best-effort */
  }
}

export function reset() {
  if (!initialized) return;
  try {
    posthog.reset();
  } catch {
    /* best-effort */
  }
}

/** `timestamp` backdates a replayed event to when it actually happened. */
export function track(event: string, props?: Record<string, unknown>, options?: { timestamp?: Date }) {
  if (!initialized || !event) return;
  try {
    posthog.capture(event, props, options?.timestamp ? { timestamp: options.timestamp } : undefined);
  } catch {
    /* best-effort */
  }
}
